import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import BoardModal, { BMCancelButton, BMDangerButton } from './BoardModal';
import { Tag, TagPool, countTagUsage, deleteTag } from '../services/tagService';

// Confirmation modal for tag deletion, shared by Project Settings
// (Project-scoped tags only) and the Tenant Admin Panel (any scope). Shows
// the live junction usage count before asking for confirmation, then
// cascades through deleteTag(): junction rows first, pool record last —
// no orphaned junctions.

interface Props {
  pca: IPublicClientApplication;
  pool: TagPool;
  tag: Tag;
  /** Admin deleting a Tenant-scoped tag — wording reflects tenant-wide impact. */
  tenantWide?: boolean;
  onDeleted: () => void;
  onClose: () => void;
}

export default function DeleteTagModal({ pca, pool, tag, tenantWide, onDeleted, onClose }: Props) {
  const [count, setCount] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    countTagUsage(pca, pool, tag.id)
      .then(n => { if (!cancelled) setCount(n); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load usage count'); });
    return () => { cancelled = true; };
  }, [pca, pool, tag.id]);

  const confirm = async () => {
    if (count === null || deleting) return;
    setDeleting(true); setError(null);
    try {
      await deleteTag(pca, pool, tag.id);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      setDeleting(false);
    }
  };

  const noun = pool.key === 'task' ? 'task' : 'decision';

  return (
    <BoardModal open title={`Delete tag "${tag.name}"`} onClose={() => !deleting && onClose()}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={deleting} />
        <BMDangerButton onClick={confirm} disabled={deleting || count === null}>
          {deleting ? 'Deleting…' : 'Delete Tag'}
        </BMDangerButton>
      </>}>
      <div style={{ fontSize: 13, color: '#5A6272', lineHeight: 1.5 }}>
        {count === null && !error ? (
          'Checking usage…'
        ) : count !== null ? (
          <>
            This tag is used on <strong style={{ color: '#0F1117' }}>{count}</strong> {noun}{count === 1 ? '' : 's'}
            {tenantWide ? ' across the tenant' : ''}. Deleting it will remove it
            {tenantWide ? ' everywhere' : ' from all of them'}. This cannot be undone.
          </>
        ) : null}
      </div>
      {error && <div style={{ marginTop: 10, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}
